import { faListCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type ReactElement, useEffect, useMemo, useRef } from 'react'
import {
  applicantModalAsideSection,
  applicantModalAsideSectionTitle
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { themeClass } from '~/styles/theme.css'
import type { AsideActionPosition } from '~/types/global'
import { useOpenTaskAside } from '../../hooks/useOpenTaskAside'
import { useTasks } from '../../queries/tasks'
import { TaskCard } from '../TaskCard/TaskCard'
import { taskGridWithoutPersons } from '../TaskCard/TaskCard.css'

type ApplicantTasksProps = {
  applicantId: number
  /** Open the first task in the aside as soon as it is known. */
  openOnMount?: boolean
  /** Open the aside through the legacy global bridge instead of the React context. */
  legacy?: boolean
}

const MAX_TASKS = 10
const modalPositions: AsideActionPosition[] = ['bottom', 'modal']

/** Open tasks of an applicant, soonest first, as standard task rows without the persons column. */
export const ApplicantTasks = ({
  applicantId,
  openOnMount = false,
  legacy = false
}: ApplicantTasksProps): ReactElement | null => {
  const { data } = useTasks({
    applicants: [applicantId],
    isCompleted: false,
    itemsPerPage: MAX_TASKS,
    enabled: applicantId > 0
  })
  const tasks = useMemo(() => data?.pages[0]?.['hydra:member'] ?? [], [data])
  const openTaskAside = useOpenTaskAside()
  const openedTaskId = useRef<number | null>(null)
  const firstTask = tasks[0]

  useEffect(() => {
    if (openOnMount && firstTask && openedTaskId.current !== firstTask.id) {
      openedTaskId.current = firstTask.id
      openTaskAside(firstTask, {
        position: legacy ? 'sidebar' : 'bottom',
        availablePositions: legacy ? ['sidebar'] : modalPositions,
        legacy
      })
    }
  }, [openOnMount, firstTask, openTaskAside, legacy])

  if (tasks.length === 0) {
    return null
  }

  return (
    <div className={clsx(applicantModalAsideSection, themeClass)}>
      <div className={applicantModalAsideSectionTitle}>
        <FontAwesomeIcon icon={faListCheck} />{' '}
        {tasks.length > 1 ? 'Prochaines tâches' : 'Prochaine tâche'}
      </div>
      <div className={taskGridWithoutPersons}>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            compact
            hidePersons
            dueDateAfterTitle
            asidePosition={legacy ? 'sidebar' : 'bottom'}
            asideAvailablePositions={legacy ? ['sidebar'] : modalPositions}
            legacyAside={legacy}
          />
        ))}
      </div>
    </div>
  )
}
